import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PrintEntryButton } from "@/components/PrintEntryButton";
import { useAuth } from "@/context/AuthContext";
import { getTerms } from "@/lib/terms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Pencil, Fuel as FuelIcon, Gauge, Leaf, PoundSterling, Droplet, FileDown, Trophy, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Field, Empty } from "@/pages/Vehicles";
import { useMeasuredWidth } from "@/lib/useChartReady";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const empty = { vehicle_reg: "", fill_type: "diesel", fill_date: new Date().toISOString().slice(0, 10), litres: "", cost: "", odometer: "", notes: "" };

export function FuelPanel() {
  const { user } = useAuth();
  const terms = getTerms(user?.region);
  const cur = user?.region === "IE" ? "€" : "£";
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ vehicles: [], totals: {} });
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("all"); // all | diesel | adblue
  const [reportOpen, setReportOpen] = useState(false);
  const [rpt, setRpt] = useState({ from_date: "", to_date: "", vehicle_reg: "" });
  const [downloading, setDownloading] = useState(false);
  const [econ, setEcon] = useState({ from_date: "", to_date: "" });

  const load = async () => {
    const [f, v, t] = await Promise.all([api.get("/fuel"), api.get("/vehicles"), api.get("/trailers")]);
    setItems(f.data);
    setAssets([...v.data.map((x) => x.registration), ...t.data.map((x) => x.trailer_number)].filter(Boolean));
  };
  const loadSummary = async (range) => {
    const r = range || econ;
    const params = new URLSearchParams();
    if (r.from_date) params.set("from_date", r.from_date);
    if (r.to_date) params.set("to_date", r.to_date);
    const s = await api.get(`/fuel/summary${params.toString() ? `?${params}` : ""}`);
    setSummary(s.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSummary(); }, [econ]);

  const openNew = (type) => { setForm({ ...empty, fill_type: type || "diesel" }); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...empty, ...r, fill_date: r.fill_date || "" }); setEditId(r.id); setOpen(true); };
  const num = (v) => (v === "" || v == null ? 0 : Number(v));

  const save = async (e) => {
    e.preventDefault();
    if (!form.vehicle_reg) { toast.error("Select a vehicle"); return; }
    const payload = { ...form, litres: num(form.litres), cost: num(form.cost), odometer: num(form.odometer), fill_date: form.fill_date || null };
    try {
      if (editId) await api.put(`/fuel/${editId}`, payload);
      else await api.post("/fuel", payload);
      toast.success(editId ? "Fill updated" : `${form.fill_type === "adblue" ? "AdBlue" : "Diesel"} fill added`);
      setOpen(false); load(); loadSummary();
    } catch { toast.error("Could not save fill"); }
  };
  const remove = async (id) => { await api.delete(`/fuel/${id}`); toast.success("Record removed"); load(); loadSummary(); };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (rpt.from_date) params.set("from_date", rpt.from_date);
      if (rpt.to_date) params.set("to_date", rpt.to_date);
      if (rpt.vehicle_reg) params.set("vehicle_reg", rpt.vehicle_reg);
      const res = await api.get(`/fuel/report?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `fuel-report.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
      setReportOpen(false);
    } catch { toast.error("Could not generate report"); }
    finally { setDownloading(false); }
  };

  const t = summary.totals || {};
  const ranked = [...(summary.vehicles || [])].sort((a, b) => {
    const am = a.avg_mpg, bm = b.avg_mpg;
    if (am == null && bm == null) return 0;
    if (am == null) return 1;
    if (bm == null) return -1;
    return bm - am;
  });
  const filtered = items.filter((r) => tab === "all" || (r.fill_type || "diesel") === tab);
  const isAdblueForm = form.fill_type === "adblue";

  return (
    <div data-testid="fuel-page">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex gap-2" data-testid="fuel-type-tabs">
          {[["all", "All fills"], ["diesel", "Diesel"], ["adblue", "AdBlue"]].map(([k, l]) => (
            <button key={k} data-testid={`fuel-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="fuel-report-button" variant="outline" onClick={() => { setRpt({ from_date: "", to_date: "", vehicle_reg: "" }); setReportOpen(true); }} className="rounded-md gap-2 border-slate-300"><FileDown size={16} /> Generate report</Button>
          <Button data-testid="add-adblue-button" variant="outline" onClick={() => openNew("adblue")} className="rounded-md gap-2 border-slate-300"><Droplet size={16} /> Add AdBlue</Button>
          <Button data-testid="add-fuel-button" onClick={() => openNew("diesel")} className="bg-black hover:bg-slate-800 rounded-md gap-2"><FuelIcon size={16} /> Add Diesel</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="fuel-totals">
        <Stat icon={FuelIcon} label="Diesel used" value={`${t.diesel_litres || 0} L`} sub={`${cur}${t.diesel_cost || 0} · ${t.diesel_fills || 0} fills`} />
        <Stat icon={Droplet} label="AdBlue used" value={`${t.adblue_litres || 0} L`} sub={`${cur}${t.adblue_cost || 0} · ${t.adblue_fills || 0} fills`} />
        <Stat icon={Gauge} label="Fleet avg MPG" value={t.avg_mpg != null ? `${t.avg_mpg}` : "—"} sub={`${t.avg_l_per_100km != null ? `${t.avg_l_per_100km} L/100km` : `${t.miles || 0} mi`}`} />
        <Stat icon={Leaf} label="CO₂ emitted" value={`${t.co2_tonnes || 0} t`} sub={`${t.co2_kg || 0} kg`} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="fuel-economy-range">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Economy period</p>
          <p className="text-xs text-slate-500">Pick any two dates for a real MPG / L/100km guide per vehicle</p>
        </div>
        <div className="flex items-end gap-2 ml-auto">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold block mb-1">From</label>
            <Input data-testid="econ-from" type="date" value={econ.from_date} onChange={(e) => setEcon({ ...econ, from_date: e.target.value })} className="h-9 w-40" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold block mb-1">To</label>
            <Input data-testid="econ-to" type="date" value={econ.to_date} onChange={(e) => setEcon({ ...econ, to_date: e.target.value })} className="h-9 w-40" />
          </div>
          {(econ.from_date || econ.to_date) && (
            <Button data-testid="econ-clear" variant="outline" className="h-9 border-slate-300" onClick={() => setEcon({ from_date: "", to_date: "" })}>Clear</Button>
          )}
        </div>
      </div>

      {ranked.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6" data-testid="fuel-league">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
            <Trophy size={16} className="text-amber-500" />
            <h3 className="font-heading font-bold text-sm tracking-tight">Economy Leaderboard ({terms.authority}){(econ.from_date || econ.to_date) ? ` · ${econ.from_date || "start"} → ${econ.to_date || "today"}` : ""}</h3>
            <span className="ml-auto text-[11px] text-slate-400 font-semibold uppercase tracking-wider hidden sm:inline">Most efficient first</span>
          </div>
          <div className="divide-y divide-slate-100">
            {ranked.map((v, i) => {
              const hasMpg = v.avg_mpg != null;
              const rank = hasMpg ? i + 1 : null;
              const medal = rank === 1 ? "bg-amber-100 text-amber-700 border-amber-300"
                : rank === 2 ? "bg-slate-200 text-slate-700 border-slate-300"
                : rank === 3 ? "bg-orange-100 text-orange-700 border-orange-300"
                : "bg-slate-50 text-slate-400 border-slate-200";
              return (
                <div key={v.vehicle_reg} data-testid="fuel-vehicle-row" className={`flex items-center justify-between px-5 py-2.5 text-sm ${rank === 1 ? "bg-amber-50/60" : ""}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span data-testid="fuel-rank" className={`inline-flex items-center justify-center w-6 h-6 rounded-full border text-xs font-bold shrink-0 ${medal}`}>{rank ?? "—"}</span>
                    <span className="font-semibold text-slate-800 truncate">{v.vehicle_reg}</span>
                    {rank === 1 && <span data-testid="fuel-top-badge" className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-amber-500 text-white hidden sm:inline">Most efficient</span>}
                  </div>
                  <div className="flex items-center gap-5 text-slate-500 text-xs">
                    <span><b className="text-slate-800 text-sm">{v.avg_mpg ?? "—"}</b> mpg</span>
                    <span><b className="text-slate-800 text-sm">{v.avg_l_per_100km ?? "—"}</b> L/100km</span>
                    <span>{v.diesel_litres} L diesel</span>
                    <span>{v.co2_kg} kg CO₂</span>
                    <span>{v.cost_per_mile != null ? `${cur}${v.cost_per_mile}/mi` : "—"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <EfficiencyDigest />

      {filtered.length === 0 ? <Empty icon={FuelIcon} text="No fills yet. Log diesel and AdBlue separately to track MPG, CO₂ and usage." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((r) => {
            const adblue = (r.fill_type || "diesel") === "adblue";
            return (
              <div key={r.id} data-testid="fuel-card" className="bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-1 hover:shadow-sm hover:border-slate-300 transition-all duration-200 animate-in-up">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-lg text-slate-900">{r.vehicle_reg}</h3>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${adblue ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{adblue ? "AdBlue" : "Diesel"}</span>
                    </div>
                    <p className="text-xs text-slate-500">{r.fill_date || "—"}{r.odometer ? ` · ${r.odometer} mi` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button data-testid="edit-fuel-button" onClick={() => openEdit(r)} title="Edit this fill"
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors">
                      <Pencil size={13} /> Edit
                    </button>
                    <PrintEntryButton kind="fuel" id={r.id} variant="icon" />
                    <button data-testid="delete-fuel-button" onClick={() => remove(r.id)} title="Delete this fill" className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <KV label="Litres" value={`${r.litres || 0} L`} strong />
                  <KV label="Cost" value={`${cur}${r.cost || 0}`} />
                  {!adblue && <KV label="MPG" value={r.mpg != null ? `${r.mpg}` : "—"} strong />}
                  {!adblue && <KV label="L/100km" value={r.l_per_100km != null ? `${r.l_per_100km}` : "—"} />}
                  {!adblue && <KV label="CO₂" value={`${r.co2_kg || 0} kg`} />}
                  {!adblue && <KV label="Miles" value={r.miles != null ? `${r.miles}` : "—"} />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Fill" : "Add Fill"}</DialogTitle><DialogDescription>Diesel fills calculate MPG (from miles between fills) and CO₂ at 2.64 kg/L. AdBlue is tracked as usage only.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Fill type *">
              <Select value={form.fill_type} onValueChange={(v) => setForm({ ...form, fill_type: v })}>
                <SelectTrigger data-testid="fuel-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="diesel">Diesel</SelectItem><SelectItem value="adblue">AdBlue</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Vehicle *">
              <Select value={form.vehicle_reg} onValueChange={(v) => setForm({ ...form, vehicle_reg: v })}>
                <SelectTrigger data-testid="fuel-vehicle"><SelectValue placeholder={assets.length ? "Select vehicle" : "Add a vehicle first"} /></SelectTrigger>
                <SelectContent>{assets.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date"><Input data-testid="fuel-date" type="date" value={form.fill_date} onChange={(e) => setForm({ ...form, fill_date: e.target.value })} /></Field>
              <Field label={`${isAdblueForm ? "AdBlue" : "Diesel"} (litres)`}><Input data-testid="fuel-litres" type="number" step="0.01" value={form.litres} onChange={(e) => setForm({ ...form, litres: e.target.value })} /></Field>
            </div>
            {!isAdblueForm && (
              <Field label="Odometer (miles)"><Input data-testid="fuel-odometer" type="number" step="1" value={form.odometer} onChange={(e) => setForm({ ...form, odometer: e.target.value })} placeholder="e.g. 128400 — needed for MPG" /></Field>
            )}
            <Field label={`Cost (${cur})`}><Input data-testid="fuel-cost" type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></Field>
            <Field label="Notes"><Input data-testid="fuel-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            <DialogFooter><Button data-testid="save-fuel-button" type="submit" className="bg-black hover:bg-slate-800">{editId ? "Save Changes" : "Add Fill"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Generate Fuel Report</DialogTitle><DialogDescription>Download a branded PDF of diesel & AdBlue usage, MPG, CO₂ and spend. Leave dates blank for all-time.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="From"><Input data-testid="report-from" type="date" value={rpt.from_date} onChange={(e) => setRpt({ ...rpt, from_date: e.target.value })} /></Field>
              <Field label="To"><Input data-testid="report-to" type="date" value={rpt.to_date} onChange={(e) => setRpt({ ...rpt, to_date: e.target.value })} /></Field>
            </div>
            <Field label="Vehicle">
              <Select value={rpt.vehicle_reg || "all"} onValueChange={(v) => setRpt({ ...rpt, vehicle_reg: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="report-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All vehicles</SelectItem>{assets.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <DialogFooter><Button data-testid="download-report-button" onClick={downloadReport} disabled={downloading} className="bg-black hover:bg-slate-800 gap-2"><FileDown size={16} /> {downloading ? "Generating…" : "Download PDF"}</Button></DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 animate-in-up">
      <div className="flex items-center gap-1.5 text-slate-400 mb-1"><Icon size={14} /><span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span></div>
      <p className="font-heading font-black text-2xl text-slate-900 tracking-tight">{value}</p>
      <p className="text-xs text-slate-400">{sub}</p>
    </div>
  );
}

function KV({ label, value, strong }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</p>
      <p className={strong ? "font-bold text-slate-900" : "text-slate-700"}>{value}</p>
    </div>
  );
}

function EfficiencyDigest() {
  const [data, setData] = useState(null);
  const [chartRef, chartWidth] = useMeasuredWidth();

  useEffect(() => {
    api.get("/fuel/economy-trend?months=12")
      .then((r) => setData(r.data))
      .catch(() => setData({ months: [], champions: [], has_data: false }));
  }, []);

  if (data === null) return null;
  const months = data.months || [];
  const withTop = months.filter((m) => m.top).slice().reverse();
  const champions = data.champions || [];

  return (
    <div data-testid="efficiency-digest" className="bg-white border border-slate-200 rounded-md p-6 mb-6 animate-in-up">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp size={18} className="text-slate-900" />
        <h3 className="font-heading font-bold text-lg tracking-tight">Efficiency Digest</h3>
      </div>
      <p className="text-sm text-slate-400 mb-4">Fleet MPG trend and the most efficient vehicle each month — last 12 months.</p>

      {!data.has_data ? (
        <div data-testid="digest-empty" className="py-10 text-center text-slate-400 text-sm">
          No monthly economy yet. Log diesel fills with odometer readings and your MPG trend plus each month's top performer will appear here.
        </div>
      ) : (
        <>
          {champions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="digest-champions">
              {champions.map((c) => (
                <span key={c.vehicle_reg} data-testid="digest-champion-chip"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-amber-50 text-amber-800 border border-amber-200">
                  <Trophy size={13} className="text-amber-500" /> {c.vehicle_reg}
                  <span className="text-amber-500 font-bold">· top {c.months_won}×</span>
                </span>
              ))}
            </div>
          )}

          <div ref={chartRef} className="min-w-0" style={{ width: "100%", height: 220 }} data-testid="digest-chart">
            {chartWidth > 0 && (
              <LineChart width={chartWidth} height={220} data={months} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} minTickGap={16} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}`} width={38} />
                <Tooltip
                  formatter={(v) => [v != null ? `${v} mpg` : "—", "Fleet avg"]}
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                />
                <Line type="monotone" dataKey="fleet_avg_mpg" stroke="#0f172a" strokeWidth={2.5} connectNulls dot={{ r: 3, fill: "#0f172a" }} activeDot={{ r: 5 }} />
              </LineChart>
            )}
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="digest-monthly-list">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Top performer each month</p>
            <div className="divide-y divide-slate-100">
              {withTop.map((m) => (
                <div key={m.month} data-testid="digest-month-row" className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="text-slate-500 w-20 shrink-0">{m.label}</span>
                  <span className="flex items-center gap-1.5 min-w-0 font-semibold text-slate-800">
                    <Trophy size={14} className="text-amber-500 shrink-0" />
                    <span className="truncate">{m.top.vehicle_reg}</span>
                    <span className="text-amber-600">{m.top.avg_mpg} mpg</span>
                  </span>
                  <span className="ml-auto text-xs text-slate-400 whitespace-nowrap">Fleet avg {m.fleet_avg_mpg ?? "—"} mpg</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Fuel() {
  return <FuelPanel />;
}
